import { Link } from 'react-router-dom';
import LogDisplay from '@/components/base/LogDisplay';
import { EXAMPLE_LOG_LINES, EXAMPLE_RESULT } from '@/mocks/challenges';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
      <div className="absolute inset-0 bg-grid-subtle opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-br from-bg-base via-bg-base to-bg-surface" />

      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-accent/3 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 text-center lg:text-left animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-panel border border-accent/30 text-accent text-xs font-mono font-medium uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              Daily incident training for engineers
            </div>

            <h1 className="font-sans text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary leading-tight mb-5">
              ExplainError Daily
              <span className="block text-accent mt-2">The Wordle for Engineers</span>
            </h1>

            <p className="text-text-secondary text-base md:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0 mb-8">
              Diagnose real production incidents in 60 seconds. Read the signal, choose the most
              likely failure pattern, assess severity, and decide the best next action.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto lg:mx-0 mb-8">
              <div className="bg-bg-panel border border-border-subtle rounded-xl p-4">
                <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-1">
                  Daily format
                </div>
                <div className="text-xl font-bold text-accent">1 incident</div>
                <p className="text-xs text-text-secondary mt-1">
                  One realistic production failure every day.
                </p>
              </div>

              <div className="bg-bg-panel border border-border-subtle rounded-xl p-4">
                <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-1">
                  Time to play
                </div>
                <div className="text-xl font-bold text-accent">60 sec</div>
                <p className="text-xs text-text-secondary mt-1">
                  Fast enough to fit into your daily routine.
                </p>
              </div>

              <div className="bg-bg-panel border border-border-subtle rounded-xl p-4">
                <div className="text-xs font-mono uppercase tracking-wider text-text-muted mb-1">
                  Core skill
                </div>
                <div className="text-xl font-bold text-accent">Triage</div>
                <p className="text-xs text-text-secondary mt-1">
                  Practise judgement under production-style pressure.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Link
                to="/challenge"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-accent hover:bg-accent-hover text-bg-base font-semibold rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-fill" />
                </span>
                Play today&apos;s challenge
              </Link>
              <Link
                to="/leaderboard"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-border-muted hover:border-border-active text-text-primary hover:text-text-primary font-medium rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bar-chart-line" />
                </span>
                View leaderboard
              </Link>
            </div>

            <div className="flex flex-wrap gap-6 justify-center lg:justify-start mt-10 pt-8 border-t border-border-subtle">
              {[
                { value: '1', label: 'Challenge / day' },
                { value: '3', label: 'Judgement calls' },
                { value: '25', label: 'Max score' },
              ].map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl font-bold font-mono text-accent">{stat.value}</div>
                  <div className="text-xs text-text-muted uppercase tracking-wider mt-0.5">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-shrink-0 w-full lg:w-[420px] animate-fade-in">
            <div className="bg-bg-panel border border-border-subtle rounded-xl p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono text-text-muted uppercase tracking-wider">
                  Example Incident
                </span>
                <span className="flex items-center gap-1.5 text-xs text-danger">
                  <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse" />
                  LIVE
                </span>
              </div>

              <LogDisplay lines={EXAMPLE_LOG_LINES} animated />

              <div className="mt-4 pt-4 border-t border-border-subtle">
                <div className="text-xs font-mono text-text-muted uppercase tracking-wider mb-3">
                  ExplainError Interpretation
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Classification', value: EXAMPLE_RESULT.classification },
                    { label: 'Severity', value: EXAMPLE_RESULT.severity, highlight: 'warn' },
                    { label: 'Action', value: EXAMPLE_RESULT.nextAction },
                    { label: 'Confidence', value: String(EXAMPLE_RESULT.confidence), highlight: 'success' },
                  ].map((item) => (
                    <div key={item.label} className="bg-bg-elevated rounded-md p-2.5">
                      <div className="text-xs text-text-muted mb-1">{item.label}</div>
                      <div
                        className={`text-xs font-mono font-medium truncate ${
                          item.highlight === 'warn'
                            ? 'text-warn'
                            : item.highlight === 'success'
                              ? 'text-success'
                              : 'text-text-primary'
                        }`}
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border-subtle">
                <p className="text-xs text-text-secondary leading-relaxed">
                  Practise the same kind of judgement engineers make during real incidents:
                  what failed, how serious it is, and what to do next.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}